import React from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import MarkdownLink from './MarkdownLink';
import keepBlankLines from './keepBlankLines';
import { cn } from 'lib/utils';

// base typography mirrors HtmlContent, extended with the markdown-only elements.
// no preflight runs in this project, so every element the authors can produce
// needs its own rule or it falls back to the browser's defaults (h4 = body size,
// h5 with 1.67em margins)
const MARKDOWN_CONTENT_CLASSES = [
  '[&_a]:font-medium [&_a]:text-primary-emphasis-light [&_a]:no-underline [&_a:hover]:underline',
  '[&_p]:my-3 [&_p]:first:mt-0 [&_p]:last:mb-0',
  '[&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1',
  '[&_li::marker]:font-normal [&_li::marker]:text-grey-dark [&_li>ul]:my-0.5 [&_li>ol]:my-0.5',
  // the body runs at 1rem, so every heading down to h4 sits above it; h5 keeps
  // the body size and stands out by weight alone
  '[&_h1]:mt-6 [&_h1]:mb-2 [&_h1]:text-3xl [&_h1]:font-semibold [&_h1]:text-black [&_h1]:first:mt-0',
  '[&_h2]:mt-5 [&_h2]:mb-2 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-black [&_h2]:first:mt-0',
  '[&_h3]:mt-4 [&_h3]:mb-1.5 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-black [&_h3]:first:mt-0',
  '[&_h4]:mt-4 [&_h4]:mb-1.5 [&_h4]:text-lg [&_h4]:font-semibold [&_h4]:text-black [&_h4]:first:mt-0',
  '[&_h5]:mt-3 [&_h5]:mb-1 [&_h5]:text-base [&_h5]:font-semibold [&_h5]:text-black [&_h5]:first:mt-0',
  '[&_h6]:mt-3 [&_h6]:mb-1 [&_h6]:text-sm [&_h6]:font-semibold [&_h6]:uppercase [&_h6]:tracking-wide [&_h6]:text-grey-dark [&_h6]:first:mt-0',
  '[&_code]:rounded [&_code]:bg-grey-lightest [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.9em]',
  '[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-grey-lightest [&_pre]:p-3 [&_pre_code]:bg-transparent [&_pre_code]:p-0',
  '[&_blockquote]:my-2 [&_blockquote]:border-l-4 [&_blockquote]:border-grey-lighter [&_blockquote]:pl-3 [&_blockquote]:text-grey',
  '[&_hr]:my-3 [&_hr]:border-grey-lighter',
  '[&_table]:my-2 [&_table]:w-full [&_table]:border-collapse',
  '[&_th]:border [&_th]:border-grey-lighter [&_th]:px-2 [&_th]:py-1 [&_th]:text-left',
  '[&_td]:border [&_td]:border-grey-lighter [&_td]:px-2 [&_td]:py-1',
  '[&_img]:max-w-full'
].join(' ');

const COMPACT_CLASSES =
  'text-sm [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_h1]:text-base [&_h2]:text-base [&_h3]:text-sm [&_h4]:text-sm [&_h5]:text-sm [&_h6]:text-xs';

const components = { a: MarkdownLink };
// gfm for tables and task lists; breaks so a single newline is a line break, as
// the authors type it, instead of commonmark's soft break
const remarkPlugins = [remarkGfm, remarkBreaks];

/**
 * renders authored markdown (training intros, questions, messages) as React.
 * react-markdown builds elements directly and never uses dangerouslySetInnerHTML;
 * raw html inside the markdown is emitted as text, so no sanitize-html pass is
 * needed here — the parser itself is the sanitizer. single newlines render as
 * line breaks and extra blank lines as visible gaps, as typed.
 * @param markdown - raw markdown string
 * @param className - extra classes for the wrapper
 * @param compact - tighter type and spacing, for table cells and cards
 */
export default function MarkdownContent({
  markdown,
  className,
  compact = false
}) {
  if (!markdown) return null;

  return (
    <div
      className={cn(
        MARKDOWN_CONTENT_CLASSES,
        compact && COMPACT_CLASSES,
        className
      )}
    >
      <ReactMarkdown remarkPlugins={remarkPlugins} components={components}>
        {keepBlankLines(markdown)}
      </ReactMarkdown>
    </div>
  );
}

MarkdownContent.propTypes = {
  markdown: PropTypes.string,
  className: PropTypes.string,
  compact: PropTypes.bool
};
